import { useCallback, useEffect, useMemo, useRef, useState, lazy, Suspense } from "react";
import DOMPurify from "dompurify";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus, FileText, MoreHorizontal, Trash2, Pencil, Download, ChevronDown,
  Bold, Italic, Underline, List, ListOrdered, Heading1, Heading2, Heading3,
  Check, Loader2, AlertTriangle, BookOpen, FileSpreadsheet, Table2,
} from "lucide-react";

// FASE 2 — Lazy-load del motore spreadsheet: caricato SOLO quando l'utente
// apre un foglio 📊. Non impatta panoramica/computo/altri tab.
const FoglioCalcolo = lazy(() => import("@/pages/FoglioCalcolo"));

// Sanitizzazione lato client — lista compatibile col backend (bleach).
const PURIFY_CFG = {
  ALLOWED_TAGS: [
    "p", "br", "strong", "b", "em", "i", "u", "s",
    "h1", "h2", "h3",
    "ul", "ol", "li",
    "blockquote",
    "a", "span", "div",
  ],
  ALLOWED_ATTR: ["href", "title", "target", "rel", "style"],
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^:]*$)/i,
  FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "form"],
  FORBID_ATTR: ["onerror", "onclick", "onload", "onmouseover", "onfocus", "onblur"],
};
const sanitize = (html) => DOMPurify.sanitize(html || "", PURIFY_CFG);

// -----------------------------------------------------------------
// Editor rich-text contentEditable + toolbar (nessuna dip. pesante).
// -----------------------------------------------------------------
function RichEditor({ initialHTML, onChange, editorRef }) {
  const ref = useRef(null);

  useEffect(() => {
    // Popola l'editor SOLO quando cambia initialHTML (cambio foglio).
    if (ref.current && ref.current.innerHTML !== initialHTML) {
      ref.current.innerHTML = initialHTML || "";
    }
  }, [initialHTML]);

  // Espone il nodo al parent (per flush)
  useEffect(() => {
    if (editorRef) editorRef.current = ref.current;
  }, [editorRef]);

  const exec = (cmd, arg = null) => {
    ref.current?.focus();
    // document.execCommand è deprecated ma resta l'unico modo semplice
    // per WYSIWYG senza dipendenze pesanti. Copertura browser ancora completa.
    document.execCommand(cmd, false, arg);
    onChange(ref.current?.innerHTML || "");
  };

  const onInput = () => {
    onChange(ref.current?.innerHTML || "");
  };

  // Blocca paste di HTML esterno: incolliamo solo plain text
  const onPaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
  };

  const btn = "h-8 px-2 rounded text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 inline-flex items-center gap-1";
  return (
    <div className="border border-slate-200 dark:border-slate-800 rounded-lg overflow-hidden bg-white dark:bg-slate-900" data-testid="quaderno-editor-wrap">
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60">
        <button type="button" className={btn} onClick={() => exec("bold")} title="Grassetto (Ctrl+B)" data-testid="quaderno-btn-bold"><Bold className="h-4 w-4" /></button>
        <button type="button" className={btn} onClick={() => exec("italic")} title="Corsivo (Ctrl+I)" data-testid="quaderno-btn-italic"><Italic className="h-4 w-4" /></button>
        <button type="button" className={btn} onClick={() => exec("underline")} title="Sottolineato (Ctrl+U)" data-testid="quaderno-btn-underline"><Underline className="h-4 w-4" /></button>
        <span className="mx-1 h-5 w-px bg-slate-200 dark:bg-slate-700" />
        <button type="button" className={btn} onClick={() => exec("formatBlock", "H1")} title="Titolo 1" data-testid="quaderno-btn-h1"><Heading1 className="h-4 w-4" /></button>
        <button type="button" className={btn} onClick={() => exec("formatBlock", "H2")} title="Titolo 2" data-testid="quaderno-btn-h2"><Heading2 className="h-4 w-4" /></button>
        <button type="button" className={btn} onClick={() => exec("formatBlock", "H3")} title="Titolo 3" data-testid="quaderno-btn-h3"><Heading3 className="h-4 w-4" /></button>
        <button type="button" className={btn} onClick={() => exec("formatBlock", "P")} title="Paragrafo" data-testid="quaderno-btn-p"><FileText className="h-4 w-4" /></button>
        <span className="mx-1 h-5 w-px bg-slate-200 dark:bg-slate-700" />
        <button type="button" className={btn} onClick={() => exec("insertUnorderedList")} title="Elenco puntato" data-testid="quaderno-btn-ul"><List className="h-4 w-4" /></button>
        <button type="button" className={btn} onClick={() => exec("insertOrderedList")} title="Elenco numerato" data-testid="quaderno-btn-ol"><ListOrdered className="h-4 w-4" /></button>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={onInput}
        onPaste={onPaste}
        className="quaderno-editor prose prose-slate dark:prose-invert max-w-none focus:outline-none px-6 py-6 min-h-[420px] text-[15px] leading-relaxed"
        data-testid="quaderno-editor"
        data-placeholder="Scrivi qui…"
        spellCheck
      />
      <style>{`
        .quaderno-editor:empty:before { content: attr(data-placeholder); color: #94A3B8; pointer-events: none; }
        .quaderno-editor h1 { font-size: 1.5rem; font-weight: 700; margin: 0.6em 0 0.4em; color: #0F172A; }
        .quaderno-editor h2 { font-size: 1.25rem; font-weight: 700; margin: 0.5em 0 0.3em; color: #0F172A; }
        .quaderno-editor h3 { font-size: 1.1rem; font-weight: 700; margin: 0.4em 0 0.3em; color: #334155; }
        .quaderno-editor p { margin: 0.35em 0; }
        .quaderno-editor ul { list-style: disc; padding-left: 1.4em; margin: 0.4em 0; }
        .quaderno-editor ol { list-style: decimal; padding-left: 1.6em; margin: 0.4em 0; }
        .quaderno-editor li { margin: 0.15em 0; }
        .dark .quaderno-editor h1, .dark .quaderno-editor h2 { color: #F1F5F9; }
        .dark .quaderno-editor h3 { color: #CBD5E1; }
      `}</style>
    </div>
  );
}

// -----------------------------------------------------------------
// Componente principale
// -----------------------------------------------------------------
export default function QuadernoTab({ cantiere, apiBase: apiBaseProp, scopeLabel, initialFoglioId }) {
  // FASE 3: `apiBase` opzionale — se assente, deriva da `cantiere.id`
  // (retro-compat Fase 1/2). Se passato ("/quaderno-generale"), usa quello.
  const apiBase = apiBaseProp || `/cantieri/${cantiere?.id}/quaderno`;
  const cantiereNome = cantiere?.nome || scopeLabel || "INVILUPPO";
  const cantiereIdRef = cantiere?.id || null;

  const [fogli, setFogli] = useState([]);           // lista fogli (senza contenuto)
  const [currentId, setCurrentId] = useState(null); // foglio selezionato
  const [current, setCurrent] = useState(null);     // foglio completo con contenuto
  const [saveState, setSaveState] = useState("idle"); // idle|saving|saved|error
  const [loading, setLoading] = useState(true);
  const [newFoglioOpen, setNewFoglioOpen] = useState(false);
  const [newFoglioName, setNewFoglioName] = useState("");
  const [newFoglioTipo, setNewFoglioTipo] = useState(null); // "LIBERO" | "CALCOLO"
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameName, setRenameName] = useState("");
  const [renameTarget, setRenameTarget] = useState(null); // id
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // {id, titolo}
  const [sidebarOpenMobile, setSidebarOpenMobile] = useState(false);
  const calcExportRef = useRef({ current: null });

  const debounceRef = useRef(null);
  const pendingHtmlRef = useRef(null);
  const editorNodeRef = useRef(null);
  const currentIdRef = useRef(null);
  useEffect(() => { currentIdRef.current = currentId; }, [currentId]);

  // ---------- Caricamento iniziale (auto-crea Generale se serve) ----------
  const reloadFogli = useCallback(async (selectId = null) => {
    try {
      const r = await api.get(`${apiBase}/`);
      setFogli(r.data || []);
      if (r.data && r.data.length > 0) {
        const idToLoad = selectId || currentIdRef.current || (initialFoglioId && r.data.find(x => x.id === initialFoglioId)?.id) || r.data[0].id;
        const exists = r.data.find(x => x.id === idToLoad);
        setCurrentId(exists ? idToLoad : r.data[0].id);
      } else {
        setCurrentId(null);
        setCurrent(null);
      }
    } catch (e) {
      toast.error("Errore caricamento quaderno");
    } finally {
      setLoading(false);
    }
  }, [apiBase, initialFoglioId]);

  useEffect(() => { reloadFogli(); }, [reloadFogli]);

  // ---------- Carica foglio completo quando cambia currentId ----------
  useEffect(() => {
    if (!currentId) { setCurrent(null); return; }
    // Reset immediato: evita che l'editor mantenga il DOM del foglio precedente
    // e che nuovi input vengano appesi al contenuto del foglio vecchio.
    setCurrent(null);
    // Reset anche eventuali pending non ancora flushati per il foglio nuovo
    // (i flush per il foglio vecchio sono già stati eseguiti da selectFoglio).
    pendingHtmlRef.current = null;
    let alive = true;
    api.get(`${apiBase}/fogli/${currentId}`)
      .then(r => { if (alive) { setCurrent(r.data); setSaveState("idle"); } })
      .catch(() => { if (alive) toast.error("Impossibile caricare il foglio"); });
    return () => { alive = false; };
  }, [currentId, apiBase]);

  // ---------- Autosave debounced ----------
  const scheduleSave = useCallback((html) => {
    pendingHtmlRef.current = html;
    setSaveState("saving");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => flushSave(), 800);
  }, []);

  const flushSave = useCallback(async () => {
    if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
    if (pendingHtmlRef.current === null) return;
    const html = pendingHtmlRef.current;
    const targetId = currentIdRef.current;
    if (!targetId) { pendingHtmlRef.current = null; return; }
    const clean = sanitize(html);
    try {
      const r = await api.patch(`${apiBase}/fogli/${targetId}`,
        { contenuto_html: clean });
      pendingHtmlRef.current = null;
      // Aggiorna updated_at nella lista laterale
      setFogli(prev => prev.map(f => f.id === targetId
        ? { ...f, updated_at: r.data.updated_at } : f));
      setSaveState("saved");
      // Torna "idle" dopo qualche secondo
      setTimeout(() => setSaveState(s => s === "saved" ? "idle" : s), 2500);
    } catch (e) {
      console.error("autosave fail", e);
      setSaveState("error");
    }
  }, [apiBase]);

  // Salvataggio manuale ai cambi rapidi di foglio: flush prima di caricare
  const selectFoglio = async (fid) => {
    if (currentId === fid) return;
    // Se c'è un save pendente, flushalo PRIMA di cambiare
    if (pendingHtmlRef.current !== null) {
      await flushSave();
    }
    setCurrentId(fid);
    setSidebarOpenMobile(false);
  };

  // Flush al beforeunload (chiusura tab/refresh)
  useEffect(() => {
    const handler = (e) => {
      if (pendingHtmlRef.current !== null) {
        // Best-effort synchronous save via keepalive fetch
        try {
          const url = `${process.env.REACT_APP_BACKEND_URL}/api${apiBase}/fogli/${currentIdRef.current}`;
          fetch(url, {
            method: "PATCH",
            credentials: "include",
            keepalive: true,
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ contenuto_html: sanitize(pendingHtmlRef.current) }),
          });
        } catch (_) { /* noop */ }
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [apiBase]);

  // ---------- Azioni fogli ----------
  const createFoglio = async () => {
    const t = (newFoglioName || "").trim();
    const tipo = newFoglioTipo || "LIBERO";
    if (!t) return;
    // Flush eventuale (solo per foglio libero)
    if (current?.tipo === "LIBERO" && pendingHtmlRef.current !== null) await flushSave();
    try {
      const r = await api.post(`${apiBase}/fogli`,
        { titolo: t, tipo });
      setNewFoglioOpen(false);
      setNewFoglioName("");
      setNewFoglioTipo(null);
      // ricarica e seleziona il nuovo
      await reloadFogli(r.data.id);
      setTimeout(() => {
        if (tipo === "LIBERO") editorNodeRef.current?.focus?.();
      }, 200);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore creazione foglio");
    }
  };

  const doRename = async () => {
    if (!renameTarget) return;
    const t = (renameName || "").trim();
    if (!t) { toast.error("Titolo obbligatorio"); return; }
    try {
      await api.patch(`${apiBase}/fogli/${renameTarget}`, { titolo: t });
      setRenameOpen(false);
      setFogli(prev => prev.map(f => f.id === renameTarget ? { ...f, titolo: t } : f));
      if (currentId === renameTarget) setCurrent(prev => prev ? { ...prev, titolo: t } : prev);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore rinomina");
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`${apiBase}/fogli/${deleteTarget.id}`);
      setDeleteOpen(false);
      // Se sto eliminando quello corrente → seleziona altro
      const others = fogli.filter(f => f.id !== deleteTarget.id);
      if (currentId === deleteTarget.id) {
        setCurrentId(others[0]?.id || null);
      }
      setFogli(others);
      // Se è rimasto vuoto, ricarico per auto-creare "Generale"
      if (others.length === 0) {
        await reloadFogli();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore eliminazione");
    }
  };

  // ---------- Export PDF ----------
  const downloadPdf = async (mode) => {
    try {
      // Flush prima di esportare
      if (pendingHtmlRef.current !== null) await flushSave();
      const params = mode === "all" ? { all: true } : { foglio_id: currentId };
      const res = await api.get(`${apiBase}/export-pdf`, { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = mode === "all"
        ? `Quaderno_${cantiereNome || "cantiere"}_completo.pdf`
        : `Quaderno_${cantiereNome || "cantiere"}_${current?.titolo || "foglio"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Errore export PDF");
    }
  };

  // Export XLSX (solo fogli CALCOLO)
  const downloadXlsx = async () => {
    try {
      if (calcExportRef.current?.current) {
        await calcExportRef.current.current();
        return;
      }
      // Fallback diretto (se ref non ancora legato)
      const res = await api.get(`${apiBase}/fogli/${currentId}/export-xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Quaderno_${cantiereNome || "cantiere"}_${current?.titolo || "foglio"}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Errore export XLSX");
    }
  };

  // ---------- Rendering ----------
  const currentInitialHTML = useMemo(() => current?.contenuto_html || "", [current?.id]);

  const StatusBadge = () => {
    if (saveState === "saving") return <span className="inline-flex items-center gap-1.5 text-xs text-slate-500" data-testid="quaderno-status-saving"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Salvataggio…</span>;
    if (saveState === "saved") return <span className="inline-flex items-center gap-1.5 text-xs text-emerald-600" data-testid="quaderno-status-saved"><Check className="h-3.5 w-3.5" /> Salvato</span>;
    if (saveState === "error") return <span className="inline-flex items-center gap-1.5 text-xs text-red-600" data-testid="quaderno-status-error"><AlertTriangle className="h-3.5 w-3.5" /> Non salvato</span>;
    return null;
  };

  return (
    <div data-testid="quaderno-root">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-amber-600" />
          <h2 className="text-lg font-bold tracking-tight">Quaderno</h2>
          <span className="text-xs text-slate-500">— appunti liberi del cantiere</span>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="quaderno-export-btn">
                <Download className="h-4 w-4 mr-1" /> Esporta <ChevronDown className="h-3.5 w-3.5 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => downloadPdf("single")}
                data-testid="quaderno-export-current"
                disabled={!currentId || current?.tipo !== "LIBERO"}
              >
                <FileText className="h-4 w-4 mr-2" /> Foglio corrente (PDF)
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={downloadXlsx}
                data-testid="quaderno-export-xlsx"
                disabled={!currentId || current?.tipo !== "CALCOLO"}
              >
                <FileSpreadsheet className="h-4 w-4 mr-2" /> Foglio corrente (XLSX)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => downloadPdf("all")} data-testid="quaderno-export-all" disabled={fogli.length === 0}>
                <FileText className="h-4 w-4 mr-2" /> Tutto il Quaderno (PDF)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-4">
        {/* Sidebar (desktop) + dropdown (mobile) */}
        <div className="md:block">
          {/* Mobile: dropdown selettore */}
          <div className="md:hidden mb-2 flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="flex-1 justify-between" data-testid="quaderno-mobile-select">
                  <span className="truncate">{current?.titolo || "Seleziona foglio"}</span>
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 max-h-80 overflow-auto">
                {fogli.map(f => (
                  <DropdownMenuItem key={f.id} onClick={() => selectFoglio(f.id)} data-testid={`quaderno-mobile-item-${f.id}`}>
                    {f.tipo === "CALCOLO"
                      ? <FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-600" />
                      : <FileText className="h-4 w-4 mr-2" />}
                    <span className="truncate">{f.titolo}</span>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setNewFoglioOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" /> Nuovo foglio
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Desktop: colonna laterale */}
          <Card className="hidden md:block">
            <CardContent className="p-2">
              <Button
                variant="ghost" size="sm" className="w-full justify-start mb-1 text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                onClick={() => setNewFoglioOpen(true)}
                data-testid="quaderno-btn-new"
              >
                <Plus className="h-4 w-4 mr-2" /> Nuovo foglio
              </Button>
              <div className="border-t border-slate-100 dark:border-slate-800 pt-1 space-y-0.5" data-testid="quaderno-sheet-list">
                {fogli.length === 0 && (
                  <div className="text-xs text-slate-400 p-2">Nessun foglio</div>
                )}
                {fogli.map(f => {
                  const active = f.id === currentId;
                  return (
                    <div key={f.id} className={`group flex items-center rounded-md ${active ? "bg-amber-50 dark:bg-amber-900/20 border-l-2 border-amber-500" : "hover:bg-slate-50 dark:hover:bg-slate-800/60"}`}>
                      <button
                        type="button"
                        onClick={() => selectFoglio(f.id)}
                        className={`flex-1 min-w-0 text-left px-2 py-1.5 text-sm truncate flex items-center gap-1.5 ${active ? "font-semibold text-slate-900 dark:text-slate-100" : "text-slate-700 dark:text-slate-300"}`}
                        data-testid={`quaderno-sheet-${f.id}`}
                        data-testid-tipo={f.tipo}
                        title={f.titolo}
                      >
                        {f.tipo === "CALCOLO"
                          ? <FileSpreadsheet className="h-3.5 w-3.5 flex-shrink-0 text-emerald-600" />
                          : <FileText className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" />}
                        <span className="truncate">{f.titolo}</span>
                      </button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="opacity-0 group-hover:opacity-100 h-7 w-7 flex items-center justify-center rounded hover:bg-slate-200 dark:hover:bg-slate-700" data-testid={`quaderno-sheet-menu-${f.id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => { setRenameTarget(f.id); setRenameName(f.titolo); setRenameOpen(true); }} data-testid={`quaderno-rename-${f.id}`}>
                            <Pencil className="h-4 w-4 mr-2" /> Rinomina
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { setDeleteTarget({ id: f.id, titolo: f.titolo }); setDeleteOpen(true); }} className="text-red-600" data-testid={`quaderno-delete-${f.id}`}>
                            <Trash2 className="h-4 w-4 mr-2" /> Elimina
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Area editor */}
        <div className="min-w-0">
          {loading ? (
            <div className="text-sm text-slate-500 p-6">Caricamento…</div>
          ) : !currentId ? (
            <Card><CardContent className="p-8 text-center text-slate-500">
              <BookOpen className="h-8 w-8 mx-auto mb-3 text-slate-300" />
              <p className="text-sm">Nessun foglio ancora. Clicca <b>+ Nuovo foglio</b> per iniziare.</p>
              <Button className="mt-4" size="sm" onClick={() => setNewFoglioOpen(true)} data-testid="quaderno-btn-new-empty">
                <Plus className="h-4 w-4 mr-1" /> Nuovo foglio
              </Button>
            </CardContent></Card>
          ) : !current ? (
            <div className="text-sm text-slate-400 p-6" data-testid="quaderno-loading-foglio">Caricamento foglio…</div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-2 gap-2">
                <button
                  type="button"
                  className="text-xl font-bold tracking-tight px-1 py-0.5 rounded hover:bg-slate-50 dark:hover:bg-slate-800 min-w-0 truncate text-left inline-flex items-center gap-2"
                  onClick={() => { setRenameTarget(current.id); setRenameName(current.titolo); setRenameOpen(true); }}
                  data-testid="quaderno-current-title"
                  title="Clicca per rinominare"
                >
                  {current.tipo === "CALCOLO"
                    ? <FileSpreadsheet className="h-5 w-5 text-emerald-600" />
                    : <FileText className="h-5 w-5 text-slate-400" />}
                  <span className="truncate">{current.titolo}</span>
                </button>
              </div>
              {current.tipo === "CALCOLO" ? (
                <Suspense fallback={<div className="text-sm text-slate-400 p-6" data-testid="quaderno-calcolo-suspense">Caricamento motore foglio di calcolo…</div>}>
                  <FoglioCalcolo
                    key={current.id}
                    apiBase={apiBase}
                    foglioId={current.id}
                    onSaveStateChange={setSaveState}
                    onExportRequest={calcExportRef.current}
                  />
                </Suspense>
              ) : (
                <>
                  <RichEditor
                    key={current.id}
                    initialHTML={currentInitialHTML}
                    onChange={scheduleSave}
                    editorRef={editorNodeRef}
                  />
                  <div className="text-[11px] text-slate-400 mt-2">
                    Il testo si salva automaticamente mentre scrivi.
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Dialog: nuovo foglio */}
      <Dialog open={newFoglioOpen} onOpenChange={(o) => { setNewFoglioOpen(o); if (!o) { setNewFoglioTipo(null); setNewFoglioName(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuovo foglio</DialogTitle>
            <DialogDescription>
              {newFoglioTipo == null
                ? "Che tipo di foglio vuoi creare?"
                : `Dai un nome al foglio ${newFoglioTipo === "CALCOLO" ? "di calcolo" : "libero"}.`}
            </DialogDescription>
          </DialogHeader>
          {newFoglioTipo == null ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
              <button
                type="button"
                onClick={() => setNewFoglioTipo("LIBERO")}
                className="text-left border border-slate-200 dark:border-slate-800 rounded-lg p-4 hover:border-amber-400 hover:bg-amber-50/50 dark:hover:bg-amber-900/10 transition"
                data-testid="quaderno-new-type-libero"
              >
                <div className="flex items-center gap-2 font-semibold text-slate-900 dark:text-slate-100 mb-1">
                  <FileText className="h-5 w-5 text-slate-500" /> 📄 Foglio libero
                </div>
                <p className="text-xs text-slate-500">Per appunti, verbali, testi ed elenchi.</p>
              </button>
              <button
                type="button"
                onClick={() => setNewFoglioTipo("CALCOLO")}
                className="text-left border border-slate-200 dark:border-slate-800 rounded-lg p-4 hover:border-emerald-400 hover:bg-emerald-50/50 dark:hover:bg-emerald-900/10 transition"
                data-testid="quaderno-new-type-calcolo"
              >
                <div className="flex items-center gap-2 font-semibold text-slate-900 dark:text-slate-100 mb-1">
                  <FileSpreadsheet className="h-5 w-5 text-emerald-600" /> 📊 Foglio di calcolo
                </div>
                <p className="text-xs text-slate-500">Per tabelle, quantità, prezzi, conteggi e formule.</p>
              </button>
            </div>
          ) : (
            <>
              <Input
                value={newFoglioName}
                onChange={(e) => setNewFoglioName(e.target.value)}
                placeholder={newFoglioTipo === "CALCOLO"
                  ? "Es. Extra lavori, Confronto preventivi…"
                  : "Es. Sopralluogo 28/09, Materiali, Riunione DL…"}
                autoFocus
                onKeyDown={(e) => { if (e.key === "Enter") createFoglio(); }}
                data-testid="quaderno-new-name-input"
              />
              <DialogFooter>
                <Button variant="ghost" onClick={() => setNewFoglioTipo(null)}>Indietro</Button>
                <Button onClick={createFoglio} className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold" data-testid="quaderno-new-confirm">
                  Crea foglio
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog: rinomina */}
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rinomina foglio</DialogTitle>
          </DialogHeader>
          <Input
            value={renameName}
            onChange={(e) => setRenameName(e.target.value)}
            autoFocus
            onKeyDown={(e) => { if (e.key === "Enter") doRename(); }}
            data-testid="quaderno-rename-input"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRenameOpen(false)}>Annulla</Button>
            <Button onClick={doRename} data-testid="quaderno-rename-confirm">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AlertDialog: elimina */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il foglio?</AlertDialogTitle>
            <AlertDialogDescription>
              Il foglio <b>{deleteTarget?.titolo}</b> verrà eliminato definitivamente insieme al suo contenuto.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="quaderno-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="quaderno-delete-confirm">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
